import React from "react";
import { Input as HeroInput, Label as HeroLabel, ErrorMessage as HeroErrorMessage } from "@heroui/react";
import cn from "classnames";

export type RegisterResult = {
  ref: (instance: HTMLInputElement | null) => void;
  name: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
};

export enum InputType {
  
}

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "className"> {
  /** Result of react-hook-form's register(name, options) */
  register: RegisterResult;
  /** Validation or server error message; shown in HeroUI ErrorMessage */
  error?: string;
  /** Label text; rendered with HeroUI Label */
  label?: string;
  /** Optional class for the wrapper */
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
}

function mergeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): (instance: T | null) => void {
  return (instance: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(instance);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T | null>).current = instance;
      }
    });
  };
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ register, error, label, className, labelClassName, inputClassName, ...props }, ref) => {
    const { ref: registerRef, ...registerRest } = register;
    const mergedRef = mergeRefs<HTMLInputElement>(registerRef, ref);
    /* UI-DESIGN: tapping the visible label must focus its field on phones;
       validation feedback also needs an explicit accessible association. */
    const generatedId = React.useId();
    const inputId = props.id ?? generatedId;
    const errorId = `${inputId}-error`;
    const hasError = error != null && error !== "";
    const describedBy = [props["aria-describedby"], hasError ? errorId : null].filter(Boolean).join(" ") || undefined;

    return (
      <div className={cn(className, "flex flex-col gap-2")}>
        {label != null && (
          <HeroLabel htmlFor={inputId} isInvalid={!!error} className={labelClassName}>
            {label}
          </HeroLabel>
        )}

        <HeroInput
          className={inputClassName}
          ref={mergedRef}
          aria-invalid={!!error}
          {...registerRest}
          {...props}
          id={inputId}
          aria-describedby={describedBy}
        />
        {hasError && (
          <HeroErrorMessage id={errorId}>{error}</HeroErrorMessage>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
